/**
 * Wireline & Slickline WL-3 helpers (fieldiq-api#1339): the end-of-job service
 * report's status skin, who may press what, its refusals as sentences, and the
 * PDF downloads (report, run, day-sheet run log).
 *
 * NOTHING HERE COMPUTES A FIGURE. Depths, footage, hours and totals are the
 * API's; this file turns them into words. A WARNING NEVER DISABLES ANYTHING:
 * the issue REFUSALS (409 job_not_completed / no_approved_sheets) arrive as the
 * mutation's error and are shown as the API's sentence.
 */
import { api } from '../api/client';
import { POPUP_BLOCKED_MESSAGE, showBlobInTab } from './blob-tab';
import { fmtQty } from './display-labels';
import { wlApiError, WL_PERMISSION_REFUSAL, type WlSkin } from './wireline-slickline';
import { wlDay, wlWarningText } from './wireline-slickline-jobs';
import type { WlWarningEntry } from '../types/wireline-slickline-jobs';

// ─── Who may press what (mirrors fieldiq-api lib/wl-roles.ts) ────────────────
//
// For hiding a button, not for enforcement: the API decides, and a 403 that
// still arrives is shown.

/** Create, refresh and issue a report (WL_DISPATCH). */
export const WL_REPORT_WRITE_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
/** Void a report (WL_ADMIN). */
export const WL_REPORT_VOID_ROLES = ['admin', 'office_staff'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
export const canWriteWlReports = inList(WL_REPORT_WRITE_ROLES);
export const canVoidWlReports = inList(WL_REPORT_VOID_ROLES);

// ─── Status ──────────────────────────────────────────────────────────────────

/** Draft steel (not yet a record), issued green (the record), void red. */
export const WL_REPORT_STATUS_SKIN: Record<string, WlSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
const UNKNOWN_SKIN: WlSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

/** An unknown status shows a slate `—` with a transparent rail; it never borrows a real status. */
export function wlReportStatusSkin(status: string | null | undefined): WlSkin {
  return status && Object.prototype.hasOwnProperty.call(WL_REPORT_STATUS_SKIN, status) ? WL_REPORT_STATUS_SKIN[status] : UNKNOWN_SKIN;
}

/** The number once issued; a draft has none yet. */
export const wlReportNumber = (r: { reportNumber: string | null; status: string }): string =>
  r.reportNumber || (r.status === 'draft' ? 'Draft' : '—');

// ─── Refusals ────────────────────────────────────────────────────────────────

/**
 * A report write's error as one sentence. Every coded refusal (job_not_completed,
 * no_approved_sheets, report_issued, report_void, report_exists, already_void,
 * signatory_unknown, the 400 on a void with no reason) carries the API's
 * sentence and is shown as sent. A bare 403 is the permission line.
 */
export function wlReportRefusal(err: unknown, fallback: string): string {
  const e = wlApiError(err);
  if (e.status === 403) return e.code && e.message ? e.message : WL_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That report no longer exists.';
  return e.message ?? fallback;
}

/** 409 report_exists: the job's live report, to open instead. */
export function wlExistingReportId(err: unknown): string | null {
  const e = wlApiError(err);
  return e.status === 409 && e.code === 'report_exists' && typeof e.extra.existingId === 'string' ? e.extra.existingId : null;
}

// ─── Words ───────────────────────────────────────────────────────────────────

/** A report warning as a sentence: the day it is about, then the shared WL sentence. */
export function wlReportWarningText(w: WlWarningEntry): string {
  const text = wlWarningText(w);
  return typeof w.workDate === 'string' && w.workDate ? `${wlDay(w.workDate)} · ${text}` : text;
}

/** `{ value, unit }` as the API sent it, in the tenant's unit; absent → `—`. Never converted here. */
export function wlReportQty(q: { value: number; unit: string } | null | undefined, digits = 2): string {
  if (!q) return '—';
  const s = fmtQty(q.value, digits);
  return s === '—' ? s : `${s} ${q.unit}`;
}

/** Two depths as an interval; either absent → that end reads `—`. */
export const wlReportInterval = (top: { value: number; unit: string } | null, bottom: { value: number; unit: string } | null) =>
  !top && !bottom ? '—' : `${wlReportQty(top)} – ${wlReportQty(bottom)}`;

// ─── PDFs ────────────────────────────────────────────────────────────────────

/**
 * Open a PDF in a tab claimed BEFORE the await (lib/blob-tab: a window.open
 * after the request is blocked as a pop-up). A sentence on failure, null on
 * success.
 */
async function openPdf(win: Window | null, load: () => Promise<{ data: Blob }>, fallback: string): Promise<string | null> {
  if (!win) return POPUP_BLOCKED_MESSAGE;
  try {
    const res = await load();
    showBlobInTab(win, res.data);
    return null;
  } catch (err) {
    win.close();
    return wlReportRefusal(err, fallback);
  }
}

/** The report's PDF: the stored file once issued, a print stamped DRAFT or VOID otherwise (the API decides). */
export const openWlReportPdf = (id: string, win: Window | null) =>
  openPdf(win, () => api.wirelineSlickline.reports.pdf(id), 'The report PDF could not be made.');
/** One run's run log. */
export const openWlRunPdf = (id: string, win: Window | null) =>
  openPdf(win, () => api.wirelineSlickline.runs.pdf(id), 'The run PDF could not be made.');
/** Every run on a day sheet, as one run log. */
export const openWlSheetRunLogPdf = (id: string, win: Window | null) =>
  openPdf(win, () => api.wirelineSlickline.sheets.runLogPdf(id), 'The run log PDF could not be made.');
